import ItemLibrary, { LibraryClickHandler } from '@/components/ItemLibrary'
import { CustomItemList } from '@/components/item/ItemList'
import Data, { treeNode } from '@/data'
import { ItemDefinition, UniqueDefinition } from '@/data/types'
import { itemCategories } from '@/library'
import { itemGetName } from '@/logic/item'
import Store from '@/store'
import { WithPassiveTooltip } from '@/tooltip'
import { TreeHandle, TreePicker } from '@/tree/TreeViewer'
import { TreeNote } from '@/types/embed'
import Dialog from '@/ui/dialog'
import Select, { OptionOrGroup } from '@/ui/select'
import { faShieldAlt, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import {
  ActionHandler,
  MarkdownEditor,
  defaultToolbar,
  getSelection,
  replaceSelection
} from '../MarkdownEditor'
import { FocusAction, TreeDataContext } from './common'

import { SearchBox } from '@/components/Search'
import './NoteEditor.scss'

type NoteCallback = (id: number) => void
type NoteSubscribe = (func: NoteCallback) => () => void

const NoteContext = React.createContext<NoteSubscribe>(() => () => {})

export function useNoteEditing() {
  const [subscriptions, setSubscriptions] = React.useState<NoteCallback[]>([])
  const onSubscribe = React.useCallback((func: NoteCallback) => {
    setSubscriptions((sub) => [...sub, func])
    return () =>
      setSubscriptions((sub) => {
        const index = sub.indexOf(func)
        if (index >= 0) {
          sub = sub.slice()
          sub.splice(index, 1)
        }
        return sub
      })
  }, [])
  const onClick = React.useCallback(
    (id: number) => {
      for (const func of subscriptions) {
        func(id)
      }
      return subscriptions.length > 0
    },
    [subscriptions]
  )
  const provider = React.useCallback(
    ({ children }: { children: ReactNode }) => (
      <NoteContext.Provider value={onSubscribe}>
        {children}
      </NoteContext.Provider>
    ),
    [onSubscribe]
  )
  return [onClick, provider, subscriptions.length > 0] as const
}

function NoteNode({
  id,
  index,
  onChange,
  setFocus
}: {
  id: number
  index: number
  onChange: (value: TreeNote | ((prev: TreeNote) => TreeNote)) => void
  setFocus: (action: FocusAction | undefined) => void
}) {
  const data = React.useContext(TreeDataContext)
  const skill = data.nodeSkill(id)
  const onRemove = React.useCallback(() => {
    onChange((note) => {
      note = { ...note }
      note.nodes = note.nodes.slice()
      note.nodes.splice(index, 1)
      return note
    })
    setFocus(undefined)
  }, [index, onChange, setFocus])
  const onMouseEnter = React.useCallback(
    () => setFocus({ nodes: [id] }),
    [id, setFocus]
  )
  const onMouseLeave = React.useCallback(() => setFocus(undefined), [setFocus])
  const onClick = React.useCallback(
    (e: React.MouseEvent) => {
      if ((e.target as HTMLElement).closest('.poe-remove')) return
      setFocus({ nodes: [id], center: true })
    },
    [id, setFocus]
  )
  if (!skill) return null
  return (
    <WithPassiveTooltip atlas={data.atlas} id={id}>
      <div
        className="poe-node"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}>
        {skill.name}
        <FontAwesomeIcon
          className="poe-remove"
          icon={faTimes}
          onClick={onRemove}
        />
      </div>
    </WithPassiveTooltip>
  )
}

const tooltipTabs = [
  { id: 'items', name: 'Basic Items' },
  { id: 'custom', name: 'Custom Items' },
  { id: 'passives', name: 'Passives' },
  { id: 'atlas', name: 'Atlas' }
]

type AscendancyOption = {
  id: string
  name: string
}

export function TreeSelector({
  atlas = false,
  onSelect
}: {
  atlas?: boolean
  onSelect: (id: number, atlas: boolean) => void
}) {
  const [search, setSearch] = React.useState('')
  const [ascendancy, setAscendancy] = React.useState<AscendancyOption>()
  const treeRef = React.useRef<TreeHandle>(null)
  const onSearch = React.useCallback(
    () => treeRef.current?.focus({ search }),
    [search]
  )
  const onClick = React.useCallback(
    (id: number) => onSelect(id, atlas),
    [onSelect, atlas]
  )
  const getOptions = React.useCallback(
    (): OptionOrGroup<AscendancyOption>[] =>
      Object.values(Data.characters).map((charData) => ({
        title: charData.name,
        options: Object.entries(charData.ascendancies).map(
          ([ascendancy, { name }]) => ({
            id: ascendancy,
            name
          })
        )
      })),
    []
  )

  return (
    <>
      <div className="poe-tree-header">
        {!atlas && (
          <Select
            options={getOptions}
            value={ascendancy}
            onChange={setAscendancy}
            allowEmpty
            placeholder="No Ascendancy"
          />
        )}
        <SearchBox
          search={search}
          setSearch={setSearch}
          onFocus={onSearch}
          placeholder={'Search...'}
        />
      </div>
      <TreePicker
        atlas={atlas}
        ascendancy={ascendancy?.id}
        onSelect={onClick}
        ref={treeRef}
      />
    </>
  )
}

export function TooltipPopout({
  handler,
  className
}: {
  handler: ActionHandler
  className?: string
}) {
  const store = Store.useStore()
  const [tab, setTab] = React.useState('items')
  const onClickItem = React.useCallback<LibraryClickHandler>(
    (e, id) =>
      handler((editor) => {
        const item: ItemDefinition | UniqueDefinition =
          Data.items[id] ?? Data.uniques[id]
        if (!item) return
        const sel = getSelection(editor!)
        const text = sel.text || item.name
        replaceSelection(
          editor,
          sel,
          `[${text}](item.${item.hash || item.id})`,
          1,
          1 + text.length
        )
      }),
    [handler]
  )
  const onClickCustom = React.useCallback(
    (id: number | undefined) =>
      id != null &&
      handler((editor) => {
        const item = store.getState().items[id]
        if (!item) return
        const sel = getSelection(editor)
        const text = itemGetName(item)
        replaceSelection(
          editor,
          sel,
          `[${text}](custom.${id})`,
          1,
          1 + text.length
        )
      }),
    [handler, store]
  )
  const onClickTree = React.useCallback(
    (id: number, atlas: boolean) =>
      handler((editor) => {
        const node = treeNode(id, Data.defaultVersion, atlas)
        if (!node) return
        const sel = getSelection(editor)
        const text = sel.text || node.name
        replaceSelection(
          editor,
          sel,
          `[${text}](${atlas ? 'atlas' : 'passive'}.${id})`,
          1,
          1 + text.length
        )
      }),
    [handler]
  )
  return (
    <div className={classNames('poe-tooltip-popout', className)}>
      <div className="poe-header">
        {tooltipTabs.map(({ id, name }) => (
          <div
            key={id}
            className={classNames('poe-tab', { 'poe-active': tab === id })}
            onClick={() => setTab(id)}>
            {name}
          </div>
        ))}
      </div>
      {tab === 'items' && (
        <ItemLibrary
          root={itemCategories}
          onClick={onClickItem}
          hideTree
          uniques
        />
      )}
      {tab === 'custom' && (
        <div className="poe-ItemLibrary">
          <div className="poe-ItemCategories">
            <CustomItemList onSelect={onClickCustom} noCreate />
          </div>
        </div>
      )}
      {tab === 'passives' && <TreeSelector onSelect={onClickTree} />}
      {tab === 'atlas' && <TreeSelector atlas onSelect={onClickTree} />}
    </div>
  )
}

export function NoteEditor({
  note,
  onChange,
  onClose,
  setFocus
}: {
  note: TreeNote
  onChange: (value: TreeNote | ((prev: TreeNote) => TreeNote)) => void
  onClose: () => void
  setFocus: (action: FocusAction | undefined) => void
}) {
  const [popout, setPopout] = React.useState<ActionHandler>()
  const onClickNode = React.useContext(NoteContext)
  React.useEffect(() => {
    return onClickNode((id) =>
      onChange((note) => {
        if (note.nodes.includes(id)) return note
        note = { ...note }
        note.nodes = note.nodes.slice()
        note.nodes.push(id)
        return note
      })
    )
  }, [onClickNode, onChange])
  const commands = React.useMemo(
    () => [
      ...defaultToolbar,
      {
        title: 'Game Tooltip',
        icon: <FontAwesomeIcon icon={faShieldAlt} />,
        className: classNames('poe-float-right', { 'poe-active': popout }),
        action: (handler: ActionHandler) =>
          setPopout((p: ActionHandler | undefined) => (p ? undefined : handler))
      }
    ],
    [popout]
  )
  const onSetText = React.useCallback(
    (text: string) => onChange((note) => ({ ...note, text })),
    [onChange]
  )
  return (
    <Dialog className="poe-NoteEditor" title="Editing note" onClose={onClose}>
      <MarkdownEditor
        value={note.text}
        onChange={onSetText}
        toolbar={commands as any}
      />
      {!!popout && <TooltipPopout handler={popout} />}
      <div className="poe-nodes">
        {note.nodes.map((id, index) => (
          <NoteNode
            key={index}
            id={id}
            onChange={onChange}
            index={index}
            setFocus={setFocus}
          />
        ))}
        {!note.nodes.length && (
          <div className="poe-empty">
            Select tree nodes to associate with this note
          </div>
        )}
      </div>
    </Dialog>
  )
}
